import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { WashingMachine, Wind, Package, Sparkle, Heart, Leaf, Truck, ShieldCheck, Clock, Star, CalendarCheck } from "@phosphor-icons/react";

const services = [
  { icon: WashingMachine, name: "Wash", color: "bg-brand-green text-white", desc: "Fresh, hygienic wash with eco detergents" },
  { icon: Wind, name: "Dry", color: "bg-brand-yellow text-brand-green", desc: "Soft, fluffy tumble dry" },
  { icon: Package, name: "Fold", color: "bg-brand-green text-white", desc: "Neatly folded, wardrobe-ready" },
];
const extras = [
  { icon: Sparkle, name: "Iron", desc: "Crisp, professional press" },
  { icon: Wind, name: "Steam", desc: "Gentle care for delicates" },
];
const why = [
  { icon: ShieldCheck, title: "Clean & Hygienic", desc: "Quality products keep your clothes fresh and spotless, every single time." },
  { icon: Clock, title: "Fast & Reliable", desc: "Quick turnaround so you have more time for the things you love." },
  { icon: Heart, title: "Care You Can Trust", desc: "We treat every item with care, as if it were our own." },
  { icon: Leaf, title: "Eco-Friendly", desc: "Environmentally conscious practices for a cleaner planet." },
];
const testimonials = [
  { name: "Sarah M.", suburb: "Parramatta", text: "The pick up and delivery is a lifesaver with two little ones at home. Clothes come back beautifully folded and smelling amazing!" },
  { name: "David T.", suburb: "Penrith", text: "Booked online in two minutes, tracked my order the whole way. Fast, friendly and my shirts have never looked crisper." },
  { name: "Priya K.", suburb: "Blacktown", text: "Love that they use eco-friendly products. The wash + dry + fold bundle is great value and the team genuinely cares." },
];

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand-yellow/20" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-soft-green" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-script inline-block -rotate-2 text-3xl font-bold text-brand-yellow-dark md:text-4xl">
              Clean Clothes. Happy Life. <Heart size={26} weight="fill" className="inline text-brand-green" />
            </span>
            <h1 className="mt-4 text-4xl font-black leading-tight tracking-tight text-brand-green sm:text-5xl lg:text-6xl">
              Laundry day, <span className="relative inline-block">done for you<svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10"><path d="M2 8 Q 100 -2 198 6" stroke="#F9C511" strokeWidth="5" fill="none" strokeLinecap="round" /></svg></span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-700 md:text-lg">
              We wash, dry, fold, iron and steam — then bring it all back to your door.
              Pick up & delivery across Western Sydney. You live, we launder.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/book" data-testid="hero-book-btn"
                className="btn-bounce rounded-full bg-brand-green px-8 py-4 text-base font-extrabold text-white shadow-[0_8px_20px_rgb(27,94,32,0.25)]">
                Book a Pick Up
              </Link>
            </div>
            <p className="mt-5 flex items-center gap-2 text-sm font-bold text-slate-500">
              <Truck size={18} weight="duotone" className="text-brand-green" /> Pick up & delivery — we come to you!
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
            <div className="absolute inset-0 -rotate-3 rounded-[3rem] bg-brand-yellow" />
            <img src="https://images.unsplash.com/photo-1709477542164-ae852db0d019?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Happy customer with fresh clean laundry" className="relative aspect-[4/3] w-full rotate-1 rounded-[3rem] object-cover shadow-xl" />
            <div className="absolute -bottom-5 left-6 flex items-center gap-2 rounded-full bg-white px-5 py-3 shadow-lg">
              <Leaf size={20} weight="duotone" className="text-brand-green" />
              <span className="text-sm font-extrabold text-brand-green">Eco-friendly, every load</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <motion.div {...fadeUp} className="text-center">
            <span className="font-script text-2xl font-bold text-brand-yellow-dark">Our Services</span>
            <h2 className="mt-1 text-3xl font-bold tracking-tight text-brand-green md:text-4xl">Everything your laundry needs</h2>
          </motion.div>
          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            {services.map((s, i) => (
              <motion.div key={s.name} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`service-card-${s.name.toLowerCase()}`}
                className="card-lift rounded-3xl bg-brand-cream p-8 text-center shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
                <span className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${s.color}`}>
                  <s.icon size={40} weight="duotone" />
                </span>
                <h3 className="mt-5 text-2xl font-semibold text-brand-green">{s.name}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-10 rounded-3xl bg-soft-yellow p-8">
            <p className="text-center font-script text-2xl font-bold text-brand-green">— Additional Services —</p>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              {extras.map((e) => (
                <div key={e.name} className="flex items-center gap-4 rounded-2xl bg-white p-5">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-brand-green">
                    <e.icon size={28} weight="duotone" />
                  </span>
                  <div>
                    <h3 className="text-lg font-extrabold text-brand-green">{e.name}</h3>
                    <p className="text-sm text-slate-600">{e.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <div className="mt-10 text-center">
            <Link to="/services" data-testid="see-pricing-link" className="font-extrabold text-brand-green underline decoration-brand-yellow decoration-4 underline-offset-4 hover:text-brand-green-light">
              See full pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <motion.div {...fadeUp} className="text-center">
            <span className="font-script text-2xl font-bold text-brand-yellow-dark">Why Choose Us? <Heart size={20} weight="fill" className="inline text-brand-green" /></span>
            <h2 className="mt-1 text-3xl font-bold tracking-tight text-brand-green md:text-4xl">Laundry with a little extra love</h2>
          </motion.div>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {why.map((w, i) => (
              <motion.div key={w.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="card-lift rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-soft-green text-brand-green">
                  <w.icon size={30} weight="duotone" />
                </span>
                <h3 className="mt-4 text-lg font-extrabold text-brand-green">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pickup & delivery */}
      <section className="bg-brand-green py-20 text-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <motion.div {...fadeUp}>
            <span className="font-script text-3xl font-bold text-brand-yellow">Pick Up & Delivery</span>
            <h2 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">We come to you!</h2>
            <ul className="mt-6 space-y-4">
              {["Easy online scheduling — pick a day that suits you", "Pick up & delivery daily between 5:00 PM and 7:00 PM", "24–48 hour turnaround — fresh laundry back fast", "Perfectly cleaned & folded with care", "FREE delivery within 10 km of Hebersham on orders over $40"].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CalendarCheck size={22} weight="duotone" className="mt-0.5 shrink-0 text-brand-yellow" />
                  <span className="text-green-50">{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/book" data-testid="delivery-book-btn"
              className="btn-bounce mt-8 inline-block rounded-full bg-brand-yellow px-8 py-4 font-extrabold text-brand-green">
              Schedule My Pick Up
            </Link>
          </motion.div>
          <motion.div {...fadeUp} className="relative">
            <div className="absolute inset-0 rotate-2 rounded-[3rem] bg-brand-yellow/30" />
            <img src="https://images.unsplash.com/photo-1694802180731-1ee2ea37692a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Zaekids delivery van on the road" className="relative aspect-[4/3] w-full -rotate-1 rounded-[3rem] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <motion.div {...fadeUp} className="text-center">
            <span className="font-script text-2xl font-bold text-brand-yellow-dark">Happy Customers</span>
            <h2 className="mt-1 text-3xl font-bold tracking-tight text-brand-green md:text-4xl">Fresh clothes, happy people</h2>
          </motion.div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`testimonial-${i}`} className="card-lift rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
                <div className="flex gap-1 text-brand-yellow">
                  {[...Array(5)].map((_, j) => <Star key={j} size={18} weight="fill" />)}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">"{t.text}"</p>
                <p className="mt-4 font-extrabold text-brand-green">{t.name} <span className="font-normal text-slate-400">· {t.suburb} NSW</span></p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <motion.div {...fadeUp} className="rounded-[3rem] bg-brand-yellow px-8 py-14 text-center">
            <h2 className="font-script text-4xl font-bold text-brand-green md:text-5xl">Thank you for choosing Zaekids Laundromat!</h2>
            <p className="mt-3 font-bold text-brand-green-dark">Fresh. Clean. Convenient. Book your first pick up today.</p>
            <Link to="/book" data-testid="cta-book-btn"
              className="btn-bounce mt-8 inline-block rounded-full bg-brand-green px-10 py-4 font-extrabold text-white">
              Book a Pick Up
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
